import { Fragment, useState } from "react"
import { Sku, SkuVersionStatus } from "../../api/models"
import AllVersionsDialog from "../AllVersionsDialog"
import MoreMenu from "./MoreMenu"
import { userPlatform } from "./platform"

// Product-page + installer download links for a product. `skus` are the
// product's skus (the ones carrying downloadMacUrl/downloadWinUrl); callers
// resolve them from wherever they have them (redux entities, API response).
// `product` only contributes the product-page url, so a sku-centric caller
// (Library) passes just its sku. Only the CURRENT platform's installer link
// renders (both when the platform can't be detected) — the other platform
// lives in the All versions dialog. allVersionsAccessToken opts in that
// quiet affordance (Library page only, which always passes ONE sku — the
// dialog is sku-scoped) — the token feeds GET /me/skus/{skuId}/versions.
// `show` narrows which installers render: a Library card for a test-version
// entitlement wants only its test build ("test"), its released siblings only
// the released ones ("released"); default "all" keeps both.
export default function ProductLinks({ product, skus, showProductPage = true, allVersionsAccessToken, show = "all" }: {
  product?: { url?: string }, skus: Sku[], showProductPage?: boolean, allVersionsAccessToken?: string,
  show?: "all" | "released" | "test"
}) {
  const macSkus = show !== "test" ? skus.filter((it) => !!it.downloadMacUrl) : []
  const winSkus = show !== "test" ? skus.filter((it) => !!it.downloadWinUrl) : []
  const hasMac = macSkus.length > 0
  const hasWin = winSkus.length > 0
  const multipleSkus = skus.length > 1
  // per-user test-build downloads (the user holds a dev/alpha/beta
  // entitlement on an unreleased version) — rendered as a separate quiet
  // link after the regular installers, labeled by the version's stage
  const testSkus = show !== "released" ? skus.filter((it) => it.testDownloadMacUrl || it.testDownloadWinUrl) : []

  const [versionsOpen, setVersionsOpen] = useState(false)

  // the dialog is the only path to the other platform's installers — it must
  // exist even with a single release, only a test version, or a narrowed
  // `show` that leaves no direct links
  const versionedSku = allVersionsAccessToken !== undefined
    ? skus.find((it) => (it.versionCount ?? 0) >= 1 || it.testVersionId)
    : undefined
  const showAllVersions = versionedSku !== undefined

  if (!hasMac && !hasWin && testSkus.length === 0 && !showAllVersions) return null

  // current-platform only: one link for the detected platform — the other
  // platform's installers live in the All versions dialog. An undetected
  // platform (no UA match) keeps both links: there is no "current" to
  // declutter to, and a single guessed link could be the wrong one.
  const shownPlatforms: ("mac" | "windows")[] = userPlatform !== null ? [userPlatform] : ["mac", "windows"]
  const labels = multipleSkus
    ? { mac: "macOS installers", windows: "Windows installers" }
    : { mac: "macOS installer", windows: "Windows installer" }

  const renderInstallerLink = (platform: "mac" | "windows", className: string) => {
    const platformSkus = platform === "mac" ? macSkus : winSkus
    if (platformSkus.length === 0) return null

    if (!multipleSkus) {
      const url = platform === "mac" ? platformSkus[0].downloadMacUrl : platformSkus[0].downloadWinUrl
      return (
        <a href={url} className={`download-link ${className}`}>
          {labels[platform]}
        </a>
      )
    }

    // several skus carry an installer: the link opens a picker
    return (
      <MoreMenu
        className={`download-link ${className}`}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: "10pt", lineHeight: "inherit" }}
        items={platformSkus.map((it) => ({ label: it.name, href: platform === "mac" ? it.downloadMacUrl! : it.downloadWinUrl! }))}
      >
        {labels[platform]}
      </MoreMenu>
    )
  }

  const [primaryLink, secondaryLink] = shownPlatforms.map((platform, i) =>
    renderInstallerLink(platform, i === 0 ? "download-link-primary" : "download-link-secondary"))

  return (
    <span className="download-links" onClick={(e) => e.stopPropagation()}>
      {showProductPage && product?.url && (
        <>
          <a href={product.url} target="_blank" rel="noopener noreferrer" className="card-link download-link-product-page" style={{ marginLeft: "auto" }} onClick={e => e.stopPropagation()}>Product page</a>
          <span className="header-separator download-sep-page" />
        </>
      )}
      <span className="download-installers">
        {primaryLink}
        {primaryLink && secondaryLink && <span className="header-separator" />}
        {secondaryLink}
      </span>
      {/* the user's test build(s) — platform-detected single link per sku,
          labeled like any other installer link (the card header already
          carries the stage and version); the tooltip keeps the stage:
          Draft = their own staging version, Alpha = the team build,
          Beta = the tester build */}
      {testSkus.map((it) => {
        const platform = (userPlatform ?? "mac") === "mac"
          ? (it.testDownloadMacUrl ? "mac" : "windows")
          : (it.testDownloadWinUrl ? "windows" : "mac")
        const url = platform === "mac" ? it.testDownloadMacUrl : it.testDownloadWinUrl
        if (!url) return null
        const stageLabel = it.testVersionStatus === SkuVersionStatus.Draft ? "draft"
          : it.testVersionStatus === SkuVersionStatus.Alpha ? "alpha"
            : "beta"
        return (
          <Fragment key={`test-${it.id}`}>
            {(hasMac || hasWin) && <span className="header-separator" />}
            <a
              href={url}
              className={`download-link ${hasMac || hasWin ? "download-link-secondary" : "download-link-primary"}`}
              title={`Your ${stageLabel} build of ${it.name}${it.testVersion ? ` (v${it.testVersion})` : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              {platform === "mac" ? "macOS installer" : "Windows installer"}
            </a>
          </Fragment>
        )
      })}
      {showAllVersions && (
        <>
          {(hasMac || hasWin || testSkus.length > 0) && <span className="header-separator" />}
          <button
            type="button"
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "9pt", opacity: 0.7 }}
            onClick={(e) => { e.stopPropagation(); setVersionsOpen(true) }}
          >
            All versions
          </button>
        </>
      )}
      {versionsOpen && allVersionsAccessToken && versionedSku && (
        <AllVersionsDialog
          sku={versionedSku}
          accessToken={allVersionsAccessToken}
          onClose={() => setVersionsOpen(false)}
        />
      )}
    </span>
  )
}
